import { Checkbox } from '@mui/material'
import { C1Tooltip } from 'c1ui'
import { C1TableReduxColumn, C1TableReduxProps } from 'components/core/list/C1Table/C1TableRedux'
import { useCallback, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'

type SelectionsMemoized<T> = {
    selectionsAsMap: Map<string, T>,
    areSomeItemsSelectedOnPage: boolean,
    areAllItemsSelectedOnPage: boolean,
};

export const useGetTableCheckboxColumn = <T, E>(
    selections: C1TableReduxProps<T, E>['selections'],
    disabledSelections: C1TableReduxProps<T, E>['disabledSelections'],
    setSelections: C1TableReduxProps<T, E>['setSelections'],
    getId: C1TableReduxProps<T, E>['getId'],
    itemsOnPage: readonly T[],
    onHeaderCheckboxClick?: (event: React.ChangeEvent<HTMLInputElement>) => void,
    isSelectingViaFilter?: boolean,
): { column: C1TableReduxColumn<T, E> | null, selectionsAsMap: Map<string, T> } => {
    const [lastSelected, setLastSelected] = useState<string | null>(null)
    const intl = useIntl()

    const disabledSelectionsAsSet = useMemo(
        () => (getId ? new Set(disabledSelections?.map(getId)) : new Set()),
        [disabledSelections, getId],
    )

    const {
        selectionsAsMap,
        areSomeItemsSelectedOnPage,
        areAllItemsSelectedOnPage,
    } = useMemo<SelectionsMemoized<T>>((): SelectionsMemoized<T> => {
        if (!selections || !getId || !itemsOnPage || !itemsOnPage.length) {
            return {
                selectionsAsMap: new Map<string, T>(),
                areSomeItemsSelectedOnPage: false,
                areAllItemsSelectedOnPage: false,
            }
        }

        const map = new Map<string, T>(selections.map((item) => [getId(item), item]))

        return {
            selectionsAsMap: map,
            areSomeItemsSelectedOnPage: itemsOnPage.some((e) => map.has(getId(e))),
            areAllItemsSelectedOnPage: itemsOnPage.every((e) => map.has(getId(e)) || disabledSelectionsAsSet.has(getId(e))),
        }
    }, [selections, getId, itemsOnPage, disabledSelectionsAsSet])

    const onSelectItem = useCallback((item: T, event): void => {
        if (!getId || !setSelections || !itemsOnPage || itemsOnPage.length === 0) return

        const currentMap = new Map(selectionsAsMap)

        setLastSelected(getId(item))

        const currentIdx = itemsOnPage.findIndex((t) => getId(t) === getId(item))
        const lastIdx = lastSelected !== undefined
            ? itemsOnPage.findIndex((t) => getId(t) === lastSelected)
            : null

        if (event.nativeEvent.shiftKey && lastSelected && lastIdx !== null) {
            const start = Math.min(currentIdx, lastIdx)
            const end = Math.max(currentIdx, lastIdx)

            // if the last selected item isn't in the current map, we just de-selected a
            // checkbox, which means holding shift should remove this range from the map instead
            const subtract = !selectionsAsMap.has(lastSelected)

            for (let i = start; i <= end; i++) {
                if (subtract) {
                    currentMap.delete(getId(itemsOnPage[i]))
                } else if (!disabledSelectionsAsSet.has(getId(itemsOnPage[i]))) {
                    currentMap.set(getId(itemsOnPage[i]), itemsOnPage[i])
                }
            }

            setSelections(Array.from(currentMap.values()))
        } else {
            if (event.target.checked) {
                currentMap.set(getId(item), item)
            } else {
                currentMap.delete(getId(item))
            }

            setSelections(Array.from(currentMap.values()))
        }
    }, [getId, setSelections, itemsOnPage, disabledSelectionsAsSet, selectionsAsMap, lastSelected])

    const onSelectAll = useCallback((event): void => {
        onHeaderCheckboxClick?.(event)

        if (!getId || !setSelections || !itemsOnPage || itemsOnPage.length === 0) return

        // If no items are able to be selected/unselected, early return
        const hasAnyEnabledItems = itemsOnPage.some((item) => !disabledSelectionsAsSet.has(getId(item)))
        if (!hasAnyEnabledItems) return

        // Make a map of everything on this page
        const newMap = new Map<string, T>()
        itemsOnPage.forEach((item) => {
            newMap.set(getId(item), item)
        })

        const currentMap = new Map(selectionsAsMap)

        if (event.target.checked) {
            // If the checkbox is checked, add everything that isn't disabled to the map
            newMap.forEach((value, key) => {
                if (!disabledSelectionsAsSet.has(key)) currentMap.set(key, value)
            })
        } else {
            // If the checkbox is unchecked, remove everything from the map
            newMap.forEach((_, key) => {
                currentMap.delete(key)
            })
        }

        setSelections(Array.from(currentMap.values()))
    }, [onHeaderCheckboxClick, getId, setSelections, itemsOnPage, selectionsAsMap, disabledSelectionsAsSet])

    const Component = useMemo(() => ({ row }) => (getId ? <Checkbox
        id={getId(row)}
        checked={isSelectingViaFilter || disabledSelectionsAsSet.has(getId(row)) || selectionsAsMap.has(getId(row))}
        inputProps={{
            'aria-label': intl.formatMessage({
                id: 'table_row_checkbox_aria_label',
                defaultMessage: 'Select item',
            }),
        }}
        value={getId(row)}
        color="primary"
        disabled={isSelectingViaFilter || disabledSelectionsAsSet.has(getId(row))}
        onChange={(event): void => {
            onSelectItem(row, event)
        }}
        sx={{ ml: 0 }}
    /> : null), [getId, isSelectingViaFilter, disabledSelectionsAsSet, selectionsAsMap, intl, onSelectItem])

    const label = useMemo(() => <C1Tooltip title={intl.formatMessage({
        id: 'table_header_checkbox_tooltip',
        defaultMessage: 'Select all items on page',
    })}
    >
        <Checkbox
            id="select-all"
            inputProps={{
                'aria-label': intl.formatMessage({
                    id: 'table_header_checkbox_aria_label',
                    defaultMessage: 'Select all items on page',
                }),
            }}
            checked={isSelectingViaFilter || areAllItemsSelectedOnPage}
            indeterminate={!isSelectingViaFilter && !areAllItemsSelectedOnPage && areSomeItemsSelectedOnPage}
            color="primary"
            onChange={(event): void => onSelectAll(event)}
            sx={{ ml: 0 }}
        />
    </C1Tooltip>, [isSelectingViaFilter, areAllItemsSelectedOnPage, areSomeItemsSelectedOnPage, intl, onSelectAll])

    if (!getId || !setSelections || !itemsOnPage || itemsOnPage.length === 0) return { column: null, selectionsAsMap: new Map() }

    return {
        column: {
            field: 'checkbox',
            width: '54px',
            loadingElement: <></>,
            label,
            Component,
        },
        selectionsAsMap,
    }
}
